import apiProtected from '@lib/api/apiClient';
import { getBody, server } from '@lib/api/http';
import { queryKeys } from '@lib/api/queryKeys';
import { useSession } from '@lib/auth/session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useProfileQuery = () => {
  const loggedIn = useSession();
  return useQuery<any>({
    queryKey: queryKeys.profile,
    queryFn: () => getBody(`${server}v1/users/get-profile`),
    enabled: loggedIn,
  });
};

const useProfileMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
};

export const useUpdateProfileMutation = () => {
  return useProfileMutation(
    (data: {
      firstName?: string;
      email?: string;
      companyName?: string;
      bio?: string;
      avatar?: string;
    }) =>
      apiProtected
        .patch(`${server}v1/users/update-profile`, {
          firstName: data.firstName,
          email: data.email,
          companyName: data.companyName,
          bio: data.bio,
          avatar: data.avatar,
        })
        .then((res) => res.data)
  );
};

export const useUpdateAddressesMutation = () => {
  return useProfileMutation((serviceAreas: unknown) =>
    apiProtected
      .patch(`${server}v1/addresses`, { serviceAreas })
      .then((res) => res.data)
  );
};

export const useUpdateExpertiseMutation = () => {
  return useProfileMutation((selectedSubCategories: unknown) =>
    apiProtected
      .patch(`${server}v1/level-of-expertise/area-of-experties`, {
        selectedSubCategories,
      })
      .then((res) => res.data)
  );
};

type LevelOfExpertiseUpdateItem = {
  id?: string | number;
  selectedValue?: number | string;
};

/** PATCH body: UserMetaData row `id` + rating. */
export const toLevelOfExpertiseUpdateBody = (
  levels: LevelOfExpertiseUpdateItem[]
): Array<{ id: string | number; selectedValue: number }> =>
  levels
    .filter((level) => level.id != null && String(level.id) !== '')
    .map((level) => ({
      id: level.id as string | number,
      selectedValue: Number(level.selectedValue ?? 0),
    }));

export const fetchProfile = () => getBody(`${server}v1/users/get-profile`);

export const useUpdateLevelMutation = () => {
  return useProfileMutation((levelOfExpertise: LevelOfExpertiseUpdateItem[]) => {
    const body = toLevelOfExpertiseUpdateBody(levelOfExpertise);
    if (body.length !== levelOfExpertise.length) {
      return Promise.reject(
        new Error('Missing expertise metadata id; refresh and try again.')
      );
    }
    return apiProtected
      .patch(`${server}v1/level-of-expertise/`, { levelOfExpertise: body })
      .then((res) => res.data);
  });
};

export const useDeleteAccountMutation = () => {
  return useMutation({
    mutationFn: () =>
      apiProtected
        .delete(`${server}v1/users/delete-me`)
        .then((res) => res.data),
  });
};

export const useMakeAddressDefaultMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiProtected.patch(
        `${server}v1/addresses/make-address-default`,
        {},
        { params: { id } }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
};

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: (input: {
      currentPassword: string;
      password: string;
      confirmPassword: string;
    }) => apiProtected.patch(`${server}v1/users/change-password`, input),
  });
};
